/**
 * Générateur — Il détermine un antécédent à partir d'une représentation
 * graphique ou d'un tableau de valeurs d'une fonction.
 * Attendu ID : 224 | Niveau : 3ème | Domaine : Fonctions (d13, O12)
 *
 * Source doc prof (B3S4/B3S5). Plan auto-validé 2026-07-16 (TODO §6) :
 * « tableau » (antécédents dans un tableau, dont une valeur à DEUX
 * antécédents — le cœur d'O12), « balayage » (encadrer un antécédent entre
 * deux entiers par changement de signe), « deux » (les deux antécédents par
 * la fonction carré, ordre croissant).
 */

import { randomChoice, randInt, shuffle, tex, makeQcm, makeTexteTrou } from '../../../utils';
import { frNb, decFrag, tableFn, NOMS_FN } from './fonctions';

const ATTENDU_ID = 224;
const L_TABLEAU = 'Lire des antécédents dans un tableau (valeur double)';
const L_BALAYAGE = 'Encadrer un antécédent par balayage';
const L_DEUX = 'Les deux antécédents par la fonction carré';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « tableau » — une valeur à DEUX antécédents ═════════

const genTableauTrous = () => {
    const nom = randomChoice(NOMS_FN);
    let xs; let ys; let vDouble; let iD1; let iD2; let iSimple;
    for (let essai = 0; essai < 80; essai++) {
        xs = shuffle([-3, -2.5, -2, -1.5, -1, -0.5, 0, 0.5, 1]).slice(0, 6).sort((a, b) => a - b);
        const pool = shuffle([-2, -1.8, -1.5, 1.4, 2, 2.5, 3]);
        vDouble = pool[0];
        const autres = pool.slice(1, 5);
        // ys : vDouble placé deux fois, les autres une fois.
        const positions = shuffle([0, 1, 2, 3, 4, 5]);
        ys = Array(6).fill(null);
        ys[positions[0]] = vDouble; ys[positions[1]] = vDouble;
        for (let k = 0; k < 4; k++) ys[positions[2 + k]] = autres[k];
        iD1 = Math.min(positions[0], positions[1]); iD2 = Math.max(positions[0], positions[1]);
        iSimple = positions[2];
        if (!xs.some(x => ys.includes(x))) break;
        vDouble = null;
    }
    if (vDouble === null) { xs = [-3, -2.5, -2, -1.5, -1, -0.5]; ys = [-1.5, -2, 1.4, -1.8, -1.5, 2]; vDouble = -1.5; iD1 = 0; iD2 = 4; iSimple = 1; }
    const phrase = `$\\text{Antécédent de } ${decFrag(ys[iSimple])} \\text{ : } {{${frNb(xs[iSimple])}}}$\n`
        + `$\\text{Antécédents de } ${decFrag(vDouble)} \\text{ : } {{${frNb(xs[iD1])}}}`
        + ` \\text{ et } {{${frNb(xs[iD2])}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Voici un tableau de valeurs de la fonction ${nom} :</p>`
        + tableFn(nom, xs.map(frNb), ys.map(frNb))
        + '<p><b>Lis les antécédents demandés</b>, du plus petit au plus grand. '
        + 'Un nombre peut avoir plusieurs antécédents.</p>',
        { ...opts(L_TABLEAU), mode: 'saisie' });
    payload._meta.params = { nom, xs, ys, vDouble };
    return payload;
};

// ═════════ « balayage » — encadrement par changement de signe (QCM) ═════════

const genBalayageQcm = () => {
    const nom = randomChoice(NOMS_FN);
    // f(x) = x² − c sur [0 ; 6] : l'antécédent de 0 est √c, encadré par des entiers.
    let c;
    do { c = randInt(2, 35); } while ([1, 4, 9, 16, 25].includes(c));
    const f = (x) => x * x - c;
    const table = tableFn(nom, [0, 1, 2, 3, 4, 5, 6].map(String), [0, 1, 2, 3, 4, 5, 6].map(x => String(f(x))));
    const k = Math.floor(Math.sqrt(c));
    const correct = `entre ${k} et ${k + 1}`;
    const autres = [`entre ${k - 1} et ${k}`, `entre ${k + 1} et ${k + 2}`, `entre 0 et 1`]
        .filter(x => x !== correct).slice(0, 3 - 1 + 1);
    const choix = [correct, ...autres].filter((v, i, t) => t.indexOf(v) === i).slice(0, 4);
    const payload = makeQcm(choix, correct,
        `<p>On pose ${tex(`${nom}(x) = x^{2} - ${c}`)}, étudiée pour x entre 0 et 6 :</p>${table}`
        // « Repère le changement de signe » était la méthode du balayage.
        + '<p><b>Entre quels entiers consécutifs se trouve l\'antécédent de 0 ?</b></p>',
        opts(L_BALAYAGE));
    payload._meta.params = { nom, c, k };
    return payload;
};

// ═════════ « deux » — les deux antécédents (fonction carré) ═════════

/**
 * Courbe de la fonction carré, graduée, avec la lecture de l'antécédent tracée
 * en pointillés depuis l'ordonnée k² jusqu'aux deux abscisses −k et k.
 * La symétrie de la courbe rend visible POURQUOI il y a deux antécédents.
 */
const svgCourbeCarre = (k) => {
    const W = 360, H = 260, ox = W / 2, oy = H - 34;
    // La courbe atteint exactement les coins hauts du cadre : au-delà de k elle
    // grimpe très vite et sortait du viewBox (tracé coupé net en haut).
    const xmax = k * 1.08;
    const ymax = xmax * xmax;
    const ux = (W / 2 - 26) / xmax;
    const uy = (oy - 20) / ymax;
    const px = (x) => ox + x * ux;
    const py = (y) => oy - y * uy;
    const pts = [];
    for (let i = -60; i <= 60; i++) {
        const x = (i / 60) * xmax;
        pts.push(`${px(x).toFixed(1)},${py(x * x).toFixed(1)}`);
    }
    // Graduation à CHAQUE entier (petits traits) et étiquette une fois sur pasX.
    // k impair → pas de 1 OBLIGATOIRE : avec un pas de 2, l'abscisse-réponse ±k
    // (7, 9, 11) ne recevrait qu'un petit trait SANS nombre, alors que la
    // consigne dit « lis sur le graphique ».
    const pasX = (k <= 6 || k % 2 === 1) ? 1 : 2;
    let ticks = '';
    for (let x = -k; x <= k; x++) {
        if (x === 0) continue;
        const majeur = x % pasX === 0;
        ticks += `<line x1="${px(x).toFixed(1)}" y1="${oy - (majeur ? 4 : 2.5)}" x2="${px(x).toFixed(1)}" y2="${oy + (majeur ? 4 : 2.5)}" stroke="#475569" stroke-width="1"/>`;
        if (majeur) {
            ticks += `<text x="${px(x).toFixed(1)}" y="${oy + 18}" text-anchor="middle" font-size="11" fill="#475569">${frNb(x)}</text>`;
        }
    }
    return '<svg xmlns="http://www.w3.org/2000/svg" '
        + `viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`
        + `<line x1="14" y1="${oy}" x2="${W - 8}" y2="${oy}" stroke="#1e293b" stroke-width="1.5"/>`
        + `<line x1="${ox}" y1="${H - 8}" x2="${ox}" y2="10" stroke="#1e293b" stroke-width="1.5"/>`
        + `<polygon points="${W - 8},${oy} ${W - 17},${oy - 4} ${W - 17},${oy + 4}" fill="#1e293b"/>`
        + `<polygon points="${ox},10 ${ox - 4},19 ${ox + 4},19" fill="#1e293b"/>`
        + ticks
        + `<text x="${ox + 6}" y="${oy + 16}" font-size="11" fill="#475569">0</text>`
        + `<text x="${W - 16}" y="${oy - 9}" font-size="12" font-style="italic" fill="#475569">x</text>`
        + `<polyline points="${pts.join(' ')}" fill="none" stroke="#1E3A8A" stroke-width="2.2"/>`
        // Ligne de niveau y = k² et ses deux points d'intersection avec la courbe.
        // Les descentes vers l'axe des abscisses ne sont PAS tracées : ce sont
        // elles qui donnent la réponse. La figure pose la question (« quelle
        // ordonnée on lit »), l'élève fait la projection et lit les abscisses.
        + `<line x1="${px(-k).toFixed(1)}" y1="${py(k * k).toFixed(1)}" x2="${px(k).toFixed(1)}" y2="${py(k * k).toFixed(1)}" stroke="#b91c1c" stroke-width="1.5" stroke-dasharray="6 4"/>`
        + `<circle cx="${px(-k).toFixed(1)}" cy="${py(k * k).toFixed(1)}" r="4" fill="#b91c1c"/>`
        + `<circle cx="${px(k).toFixed(1)}" cy="${py(k * k).toFixed(1)}" r="4" fill="#b91c1c"/>`
        + `<line x1="${ox - 5}" y1="${py(k * k).toFixed(1)}" x2="${ox + 5}" y2="${py(k * k).toFixed(1)}" stroke="#b91c1c" stroke-width="1.5"/>`
        + `<text x="${ox - 9}" y="${(py(k * k) - 5).toFixed(1)}" text-anchor="end" font-size="12" fill="#b91c1c" font-weight="600">${k * k}</text>`
        + '</svg>';
};

/**
 * L'attendu 224 demande un antécédent lu « à partir d'une représentation
 * graphique OU d'un tableau de valeurs » : sans support, l'élève calculait de
 * tête une racine carrée. Les deux supports sont proposés en variantes.
 */
const genDeuxTrous = () => {
    const nom = randomChoice(NOMS_FN);
    const k = randInt(2, 12);
    const cible = k * k;
    const phrase = `$\\text{Antécédents de } ${cible} \\text{ : } {{${-k}}} \\text{ et } {{${k}}}$`;
    const surGraphique = randInt(0, 1) === 1;

    if (surGraphique) {
        const payload = makeTexteTrou(phrase,
            `<p>Voici la courbe de la fonction ${nom}, qui associe à tout nombre son carré.</p>`
            + '{{element:0}}'
            + `<p><b>Lis sur le graphique les DEUX antécédents de ${cible}</b>, `
            + 'du plus petit au plus grand.</p>',
            {
                ...opts(L_DEUX),
                mode: 'saisie',
                elements_additionnels: {
                    version: 1,
                    elements: [{
                        type: 'image',
                        // imageUrl (forme finale inline, convention 2026-07-27) —
                        // jamais imageData pour un SVG de générateur
                        config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svgCourbeCarre(k))}` },
                    }],
                },
            });
        payload._meta.params = { nom, k, cible, support: 'courbe' };
        return payload;
    }

    // Tableau de valeurs : la cible y figure DEUX fois, en −k et en k.
    // Une abscisse PLUS GRANDE que k est toujours présente : sans elle, ±k sont
    // les colonnes extrêmes et k² la plus grande image — « réponds les deux
    // bouts » suffisait, sans rien lire.
    const grand = k + randInt(1, 2);
    const petit = randomChoice([1, 2, 3, 4, 5, 6].filter((v) => v < k));
    const abs = [...new Set([petit, k, grand])];
    const xs = [...abs.map((v) => -v), 0, ...abs].sort((a, b) => a - b);
    const payload = makeTexteTrou(phrase,
        `<p>Voici un tableau de valeurs de la fonction ${nom} :</p>`
        + tableFn(nom, xs.map(frNb), xs.map((x) => frNb(x * x)))
        + `<p><b>Lis dans le tableau les DEUX antécédents de ${cible}</b>, `
        + 'du plus petit au plus grand.</p>',
        { ...opts(L_DEUX), mode: 'saisie' });
    payload._meta.params = { nom, k, cible, support: 'tableau' };
    return payload;
};

const GENERATORS = {
    tableau_texte_trou_saisie: genTableauTrous,
    balayage_qcm_unique: genBalayageQcm,
    deux_texte_trou_saisie: genDeuxTrous,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'tableau', label: L_TABLEAU, formatsList: buildFormats('tableau') },
    { id: 'balayage', label: L_BALAYAGE, formatsList: buildFormats('balayage') },
    { id: 'deux', label: L_DEUX, formatsList: buildFormats('deux') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
